import React from 'react';
import PropTypes from 'prop-types';

import Form from './Form';
import Label from './Label';
import FormErrorInline from './FormErrorInline';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import { PhoneInput } from '../Input';
import { SubmitButton } from '../Button';
import AriaLiveRegion from '../AriaLiveRegion';

import './Form.scss';

const getCanonicalPhoneErrorMessage = () => getTranslation({
  translationKey: 'errors.mobile_number.invalid',
  fallback: 'Please enter a valid mobile number',
});

const SmsForm = (props) => {
  const {
    onSubmit,
    cta,
    countryCodes,
    defaultCountryCode,
    error,
    onFormValueChange,
    phone,
    name,
    showCaptcha,
    clientPhoneError,
    disableSubmit,
    suppressServerPhoneError,
    datadogActionName,
  } = props;

  return (
    <div className="SmsForm">
      <Form
        name="sms_form"
        onChange={onFormValueChange}
        onSubmit={onSubmit}
        error={error}
        datadogActionName={datadogActionName}
      >
        {({
          onChange, errorData, errorKeys = [], processing,
        }) => {
          const hasServerPhoneError = !!(errorData?.mobile_number);
          const shouldShowServerPhoneError = hasServerPhoneError && !suppressServerPhoneError && !processing;
          const phoneErrorRaw = clientPhoneError || (shouldShowServerPhoneError ? errorData.mobile_number : null);
          const phoneError = phoneErrorRaw ? getCanonicalPhoneErrorMessage() : null;
          const nonPhoneErrorKeys = errorKeys.filter((key) => key !== name && key !== 'country_code');
          const phoneInputErrors = phoneError
            ? [...nonPhoneErrorKeys, name, 'country_code']
            : nonPhoneErrorKeys;
          return (
            <div>
              <AriaLiveRegion
                id="SmsForm__status"
              >
                {processing ? 'Loading' : ''}
              </AriaLiveRegion>
              <Label forInput={name} required>
                <TranslationProvider
                  translationKey="mobile_number"
                >
                  Mobile Number
                </TranslationProvider>
              </Label>
              <PhoneInput
                onChange={onChange}
                countryCodes={countryCodes}
                defaultCountryCode={defaultCountryCode}
                errors={phoneInputErrors}
                phone={phone}
                name={name}
                ariaDescribedby="phone_number_error"
              />
              {phoneError && <FormErrorInline id="phone_number_error">{phoneError}</FormErrorInline>}
              {showCaptcha && <div id="captcha__container" />}
              <TranslationProvider
                translationKey="onboarding.continue"
              >
                <SubmitButton
                  processing={processing}
                  disabled={disableSubmit}
                  data-dd-action-name={datadogActionName}
                  lg
                >
                  {cta}
                </SubmitButton>
              </TranslationProvider>
            </div>
          );
        }}
      </Form>
    </div>
  );
};

SmsForm.propTypes = {
  onFormValueChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cta: PropTypes.string,
  countryCodes: PropTypes.arrayOf(PropTypes.shape({
    iso3166: PropTypes.string,
    code: PropTypes.string,
    country_name: PropTypes.string,
  })),
  defaultCountryCode: PropTypes.string,
  error: PropTypes.shape({
    serverError: PropTypes.bool,
    msg: PropTypes.string,
    keys: PropTypes.arrayOf(PropTypes.string),
  }),
  phone: PropTypes.string,
  name: PropTypes.string,
  showCaptcha: PropTypes.bool,
  clientPhoneError: PropTypes.string,
  disableSubmit: PropTypes.bool,
  suppressServerPhoneError: PropTypes.bool,
  datadogActionName: PropTypes.string,
};

SmsForm.defaultProps = {
  cta: 'Continue',
  countryCodes: undefined,
  defaultCountryCode: '1',
  error: {
    serverError: false,
    msg: '',
    keys: [],
  },
  phone: '',
  name: 'phone',
  showCaptcha: false,
  clientPhoneError: null,
  disableSubmit: false,
  suppressServerPhoneError: false,
  datadogActionName: undefined,
};

export default SmsForm;
